import { useState, useMemo, useCallback, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { DataTable } from '@/components/data/DataTable'
import { useUpdateRunbook, useUpdateRunbookLocationOptions, useUpdateRunbookTeamOptions, useUpdateRunbookStats } from '../hooks'
import { createUpdateRunBookColumns } from '../columns'
import { JsonViewDialog } from '@/components/ui/JsonViewDialog'
import { RecoverRequestDetailsDialog } from '@/features/recover-requests/components/RecoverRequestDetailsDialog'
import { ObjectQRCodeDialog } from '@/features/objects/components/ObjectQRCodeDialog'
import { ViewUpdatesDialog } from '../components/ViewUpdatesDialog'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Button } from '@/components/ui/button'
import { X, FileText, Package, MapPin } from 'lucide-react'
import type { UpdateRunBookItem } from '../api'
import { debounce } from '@/lib/utils'
import { useTimezoneDate } from '@/hooks/useTimezoneDate'

export function UpdateRunBookPage() {
    const { t } = useTranslation()
    const { i18n } = useTranslation()
    const { formatDateShort } = useTimezoneDate()
    const navigate = useNavigate()
    const [page, setPage] = useState(1)
    const [limit, setLimit] = useState(20)
    const [search, setSearch] = useState('')
    const [debouncedSearch, setDebouncedSearch] = useState('')
    const [locationFilter, setLocationFilter] = useState<string>('')
    const [teamFilter, setTeamFilter] = useState<string>('')
    const [jsonDialog, setJsonDialog] = useState<{ title: string; content: string | null } | null>(null)
    const [detailsDialog, setDetailsDialog] = useState<{ objectID: string; requestID: string } | null>(null)
    const [showQRCodeDialog, setShowQRCodeDialog] = useState(false)
    const [qrCodeObjectID, setQrCodeObjectID] = useState<string | null>(null)
    const [updatesDialog, setUpdatesDialog] = useState<{ requestID: number; objectID: string; objectDescription: string | null } | null>(null)

    const { data: locationOptions } = useUpdateRunbookLocationOptions()
    const { data: teamOptions } = useUpdateRunbookTeamOptions()
    const { data: stats, isLoading: statsLoading } = useUpdateRunbookStats()

    const debouncedSetSearch = useMemo(() => debounce((value: string) => setDebouncedSearch(value), 350), [])
    // A search still waiting when the page closes is dropped, not applied to a page that is gone.
    useEffect(() => () => debouncedSetSearch.cancel(), [debouncedSetSearch])

    useEffect(() => {
        debouncedSetSearch(search)
    }, [search, debouncedSetSearch])

    const { data: updateRunbookResponse } = useUpdateRunbook({
        page,
        perPage: limit,
        search: debouncedSearch || undefined,
        location: locationFilter || undefined,
        team: teamFilter || undefined,
    })

    const updateRunbookItems: UpdateRunBookItem[] = useMemo(() => {
        if (!updateRunbookResponse) return []
        const dataArray = updateRunbookResponse.data?.data || updateRunbookResponse.data
        if (!dataArray || !Array.isArray(dataArray)) {
            return []
        }
        return dataArray
    }, [updateRunbookResponse])

    const handleShowMetadata = useCallback((title: string, content: string | null) => {
        setJsonDialog({ title, content })
    }, [])

    const handleShowObjectDetails = useCallback((objectID: string, requestID: string) => {
        setDetailsDialog({ objectID, requestID })
    }, [])

    const handleShowQRCode = useCallback((objectID: string) => {
        setQrCodeObjectID(objectID)
        setShowQRCodeDialog(true)
    }, [])

    const handleViewUpdates = useCallback((row: UpdateRunBookItem) => {
        const requestID = row.id ?? (row.requestID != null ? Number(row.requestID) : null)
        if (requestID != null && !isNaN(requestID)) {
            setUpdatesDialog({
                requestID,
                objectID: row.objectID || '',
                objectDescription: row.objectDescription || null,
            })
        }
    }, [])

    const handleRowClick = useCallback((row: UpdateRunBookItem) => {
        const requestID = row.id ?? (row.requestID != null ? Number(row.requestID) : null)
        if (requestID != null && !isNaN(requestID)) {
            navigate(`/recovery-runbook/${requestID}`, {
                state: {
                    objectID: row.objectID,
                    objectDescription: row.objectDescription,
                    forecastStartTime: row.forecastStartTime,
                    forecastFinishTime: row.forecastFinishTime,
                    from: '/update-runbook',
                },
            })
        }
    }, [navigate])

    const columns = useMemo(
        () => createUpdateRunBookColumns(t, formatDateShort, handleShowMetadata, handleShowObjectDetails, handleShowQRCode, handleRowClick, handleViewUpdates),
        [t, formatDateShort, handleShowMetadata, handleShowObjectDetails, handleShowQRCode, handleRowClick, handleViewUpdates]
    )

    const handleExport = useCallback(() => {
        console.log('Export update runbook to CSV')
    }, [])

    const handleClearFilters = useCallback(() => {
        setLocationFilter('')
        setTeamFilter('')
        setSearch('')
        setPage(1)
    }, [])

    return (
        <div className="space-y-[var(--spacing-section)]" style={{ padding: 'var(--spacing-page)' }}>
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-xl lg:text-2xl font-bold">{t('updateRunBook.title')}</h1>
                    <p className="text-muted-foreground">
                        {t('updateRunBook.description')}
                    </p>
                </div>
            </div>

            <div className="grid gap-4 md:grid-cols-3">
                <div className="rounded-lg border bg-card p-6">
                    <div className="flex items-center space-x-2">
                        <FileText className="h-5 w-5 text-muted-foreground" />
                        <div>
                            <p className="text-sm font-medium">{t('updateRunBook.numberRequests')}</p>
                            <p className="text-2xl font-bold">
                                {statsLoading ? '...' : (stats?.uniqueRequestCount || 0)}
                            </p>
                        </div>
                    </div>
                </div>
                <div className="rounded-lg border bg-card p-6">
                    <div className="flex items-center space-x-2">
                        <Package className="h-5 w-5 text-muted-foreground" />
                        <div>
                            <p className="text-sm font-medium">{t('updateRunBook.numberObjects')}</p>
                            <p className="text-2xl font-bold">
                                {statsLoading ? '...' : (stats?.objectCount || 0)}
                            </p>
                        </div>
                    </div>
                </div>
                <div className="rounded-lg border bg-card p-6">
                    <div className="flex items-center space-x-2">
                        <MapPin className="h-5 w-5 text-muted-foreground" />
                        <div>
                            <p className="text-sm font-medium">{t('updateRunBook.numberLocations')}</p>
                            <p className="text-2xl font-bold">
                                {statsLoading ? '...' : (stats?.locationCount || 0)}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            <div
                className="overflow-x-auto [&_td]:!py-1"
                style={{
                    marginLeft: 'calc(var(--spacing-page) * -2)',
                    marginRight: 'calc(var(--spacing-page) * -2)',
                    paddingLeft: 'calc(var(--spacing-page) * 0.05)',
                    paddingRight: 'calc(var(--spacing-page) * 0.05)',
                    width: 'calc(100vw - var(--sidebar-width-expanded) - var(--spacing-page) * 0.1)',
                    maxWidth: 'none',
                    minWidth: '1200px',
                }}
            >
                <DataTable
                    columns={columns}
                    data={updateRunbookItems}
                    searchKey="objectID"
                    searchPlaceholder={t('updateRunBook.searchObjectID')}
                    searchValue={search}
                    onSearchChange={(value) => {
                        setSearch(value)
                        setPage(1)
                    }}
                    onExport={handleExport}
                    exportButtonText={t('updateRunBook.export')}
                    enableRowSelection={false}
                    onRowClick={handleRowClick}
                    pagination={{
                        page,
                        limit,
                        total: updateRunbookResponse?.data?.total || 0,
                        onPageChange: setPage,
                        onPageSizeChange: setLimit,
                    }}
                    toolbarContent={
                        <div className="flex items-end gap-2 ml-4 flex-nowrap">
                            <div className="w-[180px] flex-shrink-0 flex flex-col gap-1">
                                <label className="text-xs font-medium text-muted-foreground px-0.5">{t('updateRunBook.locationLabel', 'Location')}</label>
                                <Select value={locationFilter || 'all'} onValueChange={(value) => {
                                    setLocationFilter(value === 'all' ? '' : value)
                                    setPage(1)
                                }}>
                                    <SelectTrigger>
                                        <SelectValue placeholder={t('updateRunBook.filterLocation')} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">{t('common.all')}</SelectItem>
                                        {locationOptions?.map((location) => (
                                            <SelectItem key={location} value={location}>
                                                {location}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="w-[180px] flex-shrink-0 flex flex-col gap-1">
                                <label className="text-xs font-medium text-muted-foreground px-0.5">{t('updateRunBook.teamLabel', 'Team')}</label>
                                <Select value={teamFilter || 'all'} onValueChange={(value) => {
                                    setTeamFilter(value === 'all' ? '' : value)
                                    setPage(1)
                                }}>
                                    <SelectTrigger>
                                        <SelectValue placeholder={t('updateRunBook.filterTeam')} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">{t('common.all')}</SelectItem>
                                        {teamOptions?.map((team: string) => (
                                            <SelectItem key={team} value={team}>
                                                {team}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            {(locationFilter || teamFilter || search) && (
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={handleClearFilters}
                                    className="gap-2 flex-shrink-0"
                                >
                                    <X className="h-4 w-4" />
                                    {t('updateRunBook.clearFilters')}
                                </Button>
                            )}
                        </div>
                    }
                />
            </div>

            {jsonDialog && (
                <JsonViewDialog
                    open={!!jsonDialog}
                    onOpenChange={(open) => {
                        if (!open) setJsonDialog(null)
                    }}
                    title={jsonDialog.title}
                    content={jsonDialog.content}
                />
            )}

            {detailsDialog && (
                <RecoverRequestDetailsDialog
                    open={!!detailsDialog}
                    onOpenChange={(open) => {
                        if (!open) setDetailsDialog(null)
                    }}
                    objectID={detailsDialog.objectID}
                    requestID={detailsDialog.requestID}
                />
            )}

            <ObjectQRCodeDialog
                open={showQRCodeDialog}
                onOpenChange={(open) => {
                    setShowQRCodeDialog(open)
                    if (!open) setQrCodeObjectID(null)
                }}
                objectID={qrCodeObjectID}
            />

            {updatesDialog && (
                <ViewUpdatesDialog
                    open={!!updatesDialog}
                    onOpenChange={(open) => {
                        if (!open) setUpdatesDialog(null)
                    }}
                    requestID={updatesDialog.requestID}
                    objectID={updatesDialog.objectID}
                    objectDescription={updatesDialog.objectDescription}
                />
            )}
        </div>
    )
}
